<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import ClosureAPI from 'dashboard/api/closure';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import NextInput from 'next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import {
  Chart,
  LineElement,
  BarElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'vue-chartjs';

// SICARG MOD 3 v9 — Panel avanzado: tendencias, planes de pago, distribución de clasificaciones.
Chart.register(
  LineElement, BarElement, PointElement, CategoryScale, LinearScale,
  Tooltip, Legend, Filler
);

const { t } = useI18n();
const router = useRouter();
const store = useStore();
const { accountScopedRoute } = useAccount();
const agents = useMapGetter('agents/getAgents');

// ─── Helpers de fecha ────────────────────────────────────────────────────────
const toDateStr = d => d.toISOString().slice(0, 10);
const defaultSince = () => toDateStr(new Date(Date.now() - 29 * 86400000));
const defaultUntil = () => toDateStr(new Date());

// ─── Filtro global (gobierna TODAS las secciones del panel) ──────────────────
const closedSince = ref(defaultSince());
const closedUntil = ref(defaultUntil());
const selectedAgent = ref('');

// ─── Filtros solo para la tabla de cierres ────────────────────────────────────
const managedAmountMin = ref('');
const managedAmountMax = ref('');
const currentPage = ref(1);
const totalPages = ref(1);
const totalCount = ref(0);

// ─── Estado de carga ─────────────────────────────────────────────────────────
const isLoadingStats      = ref(false);
const isLoadingTable      = ref(false);
const isLoadingProjection = ref(false);

const stats      = ref(null);
const closures   = ref([]);
const projection = ref([]);
const projectionGroupBy = ref('week');

const filterSelectClass =
  'w-full !h-10 !min-h-0 !m-0 px-3 text-sm leading-tight rounded-lg bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-offset-[-1px] outline-n-weak border-none';

// ─── Formatters ──────────────────────────────────────────────────────────────
const formatMoney = value => {
  const n = Number(value || 0);
  return `$${n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = str => {
  if (!str) return '—';
  const [y, m, d] = str.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};

const deltaLabel = (current, previous) => {
  if (!previous || previous === 0) return '';
  const pct = ((current - previous) / previous) * 100;
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(0)}%`;
};

const deltaClass = (current, previous) => {
  if (!previous || current === previous) return 'text-n-slate-10';
  return current > previous ? 'text-n-teal-9' : 'text-n-ruby-9';
};

// ─── Chart options ───────────────────────────────────────────────────────────
const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false }, tooltip: { mode: 'index', intersect: false } },
  scales: { x: { ticks: { maxTicksLimit: 10 } } },
};

// Opciones para barras horizontales (clasificaciones)
const horizontalChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: 'y',
  plugins: { legend: { display: false }, tooltip: { mode: 'index', intersect: false } },
  scales: {
    x: { ticks: { maxTicksLimit: 6 } },
    y: { ticks: { font: { size: 11 } } },
  },
};

// ─── Chart data ──────────────────────────────────────────────────────────────
const trendData = computed(() => {
  if (!stats.value?.daily_series?.length) return null;
  return {
    labels: stats.value.daily_series.map(d => d.date.slice(5)),
    datasets: [{
      label: t('CLOSURE_PANEL.TREND.CLOSURES'),
      data: stats.value.daily_series.map(d => d.total_closures),
      borderColor: '#3b82f6',
      backgroundColor: 'rgba(59,130,246,0.10)',
      fill: true,
      tension: 0.3,
      pointRadius: 2,
    }],
  };
});

const trendAmountData = computed(() => {
  if (!stats.value?.daily_series?.length) return null;
  return {
    labels: stats.value.daily_series.map(d => d.date.slice(5)),
    datasets: [{
      label: t('CLOSURE_PANEL.TREND.AMOUNT'),
      data: stats.value.daily_series.map(d => d.total_managed_amount),
      borderColor: '#10b981',
      backgroundColor: 'rgba(16,185,129,0.10)',
      fill: true,
      tension: 0.3,
      pointRadius: 2,
    }],
  };
});

const projectionChartData = computed(() => {
  if (!projection.value.length) return null;
  return {
    labels: projection.value.map(p => p.period),
    datasets: [{
      label: t('CLOSURE_PANEL.PROJECTION.AMOUNT'),
      data: projection.value.map(p => p.amount),
      backgroundColor: 'rgba(139,92,246,0.7)',
      borderRadius: 4,
    }],
  };
});

const ranking = computed(() => {
  if (!stats.value?.amount_by_agent) return [];
  return [...stats.value.amount_by_agent].sort((a, b) => b.amount - a.amount);
});

// ─── Chart data: distribución de clasificaciones ─────────────────────────────
const classificationByReasonData = computed(() => {
  if (!stats.value?.by_reason) return null;
  const sorted = Object.entries(stats.value.by_reason)
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 12);
  if (!sorted.length) return null;
  return {
    labels: sorted.map(([r]) => r || 'sin_motivo'),
    datasets: [{
      label: t('CLOSURE_PANEL.CLASSIFICATIONS.COUNT'),
      data: sorted.map(([, c]) => c),
      backgroundColor: 'rgba(59,130,246,0.70)',
      borderRadius: 4,
    }],
  };
});

const GROUP_COLORS = {
  cobro_efectivo:    'rgba(16,185,129,0.75)',
  compromiso_futuro: 'rgba(139,92,246,0.75)',
  gestion_sin_cobro: 'rgba(245,158,11,0.75)',
  neutro:            'rgba(148,163,184,0.75)',
};
const GROUP_LABELS = {
  cobro_efectivo:    'Cobro efectivo',
  compromiso_futuro: 'Compromiso futuro',
  gestion_sin_cobro: 'Sin cobro',
  neutro:            'Neutro',
};

const classificationByGroupData = computed(() => {
  if (!stats.value?.by_group) return null;
  const entries = Object.entries(stats.value.by_group);
  if (entries.every(([, v]) => v === 0)) return null;
  return {
    labels: entries.map(([k]) => GROUP_LABELS[k] || k),
    datasets: [{
      label: t('CLOSURE_PANEL.CLASSIFICATIONS.COUNT'),
      data: entries.map(([, v]) => v),
      backgroundColor: entries.map(([k]) => GROUP_COLORS[k] || 'rgba(148,163,184,0.7)'),
      borderRadius: 4,
    }],
  };
});

// ─── Construcción de params globales ─────────────────────────────────────────
const globalParams = () => {
  const p = {};
  if (closedSince.value) p.closed_since = closedSince.value;
  if (closedUntil.value) p.closed_until = closedUntil.value;
  if (selectedAgent.value) p.user_id = selectedAgent.value;
  return p;
};

// ─── Carga de datos ──────────────────────────────────────────────────────────
const loadStats = async () => {
  isLoadingStats.value = true;
  try {
    const { data } = await ClosureAPI.getStatistics(globalParams());
    stats.value = data;
  } catch {
    useAlert(t('CLOSURE_PANEL.API.ERROR'));
  } finally {
    isLoadingStats.value = false;
  }
};

const loadTable = async () => {
  isLoadingTable.value = true;
  try {
    const p = { ...globalParams(), page: currentPage.value, per_page: 50 };
    if (managedAmountMin.value) p.managed_amount_min = managedAmountMin.value;
    if (managedAmountMax.value) p.managed_amount_max = managedAmountMax.value;
    const { data } = await ClosureAPI.listClosures(p);
    closures.value = data.data || [];
    totalPages.value = data.meta?.total_pages || 1;
    totalCount.value = data.meta?.total_count || 0;
  } catch {
    useAlert(t('CLOSURE_PANEL.API.ERROR'));
  } finally {
    isLoadingTable.value = false;
  }
};

const loadProjection = async () => {
  isLoadingProjection.value = true;
  try {
    const p = { group_by: projectionGroupBy.value };
    if (selectedAgent.value) p.user_id = selectedAgent.value;
    const { data } = await ClosureAPI.getProjection(p);
    projection.value = data.data || [];
  } catch {
    useAlert(t('CLOSURE_PANEL.API.ERROR'));
  } finally {
    isLoadingProjection.value = false;
  }
};

const applyAll = () => {
  currentPage.value = 1;
  Promise.all([loadStats(), loadTable(), loadProjection()]);
};

const resetAll = () => {
  closedSince.value     = defaultSince();
  closedUntil.value     = defaultUntil();
  selectedAgent.value   = '';
  managedAmountMin.value = '';
  managedAmountMax.value = '';
  currentPage.value     = 1;
  applyAll();
};

const prevPage = () => {
  if (currentPage.value > 1) { currentPage.value -= 1; loadTable(); }
};
const nextPage = () => {
  if (currentPage.value < totalPages.value) { currentPage.value += 1; loadTable(); }
};

const changeProjectionGroup = group => {
  projectionGroupBy.value = group;
  loadProjection();
};

onMounted(() => {
  store.dispatch('agents/get');
  applyAll();
});

const goToStats  = () => router.push(accountScopedRoute('closure_statistics_index'));
const goToConfig = () => router.push(accountScopedRoute('closure_settings_index'));
</script>

<template>
  <SettingsLayout :no-records-found="false" class="gap-6">
    <template #header>
      <BaseSettingsHeader
        :title="t('CLOSURE_PANEL.HEADER.TITLE')"
        :description="t('CLOSURE_PANEL.HEADER.DESCRIPTION')"
      >
        <template #actions>
          <NextButton faded slate sm icon="i-lucide-bar-chart-3" :label="t('CLOSURE_PANEL.STATS_LINK')" @click="goToStats" />
          <NextButton faded slate sm icon="i-lucide-settings-2" :label="t('CLOSURE_PANEL.CONFIG_LINK')" @click="goToConfig" />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="flex flex-col gap-6 mt-4">

        <!-- ── Filtro global único ────────────────────────────────────────── -->
        <div class="p-4 rounded-lg border border-n-weak bg-n-alpha-1">
          <p class="mb-3 text-xs font-medium text-n-slate-11 uppercase tracking-wide">
            {{ t('CLOSURE_PANEL.FILTER.GLOBAL_LABEL') }}
          </p>
          <div
            class="grid gap-x-3 gap-y-1.5 items-end"
            :style="{ gridAutoFlow: 'column', gridTemplateRows: 'auto auto', gridAutoColumns: '9.5rem' }"
          >
            <label class="text-xs text-n-slate-11">{{ t('CLOSURE_PANEL.FILTER.CLOSED_SINCE') }}</label>
            <NextInput v-model="closedSince" type="date" />

            <label class="text-xs text-n-slate-11">{{ t('CLOSURE_PANEL.FILTER.CLOSED_UNTIL') }}</label>
            <NextInput v-model="closedUntil" type="date" />

            <label class="text-xs text-n-slate-11">{{ t('CLOSURE_PANEL.FILTER.AGENT') }}</label>
            <select v-model="selectedAgent" :class="filterSelectClass">
              <option value="">{{ t('CLOSURE_PANEL.FILTER.ALL') }}</option>
              <option v-for="agent in agents" :key="agent.id" :value="agent.id">{{ agent.name }}</option>
            </select>
          </div>
          <div class="flex gap-2 mt-3">
            <NextButton blue sm :is-loading="isLoadingStats || isLoadingTable" :label="t('CLOSURE_PANEL.FILTER.APPLY')" @click="applyAll" />
            <NextButton faded slate sm :label="t('CLOSURE_PANEL.FILTER.RESET')" @click="resetAll" />
          </div>
        </div>

        <!-- ── Tarjetas de comparativa período seleccionado vs anterior ──── -->
        <div v-if="stats" class="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div class="p-4 rounded-lg border border-n-weak">
            <p class="text-xs text-n-slate-11">{{ t('CLOSURE_PANEL.PERIOD.COUNT') }}</p>
            <p class="text-2xl font-semibold text-n-slate-12">{{ stats.period?.count ?? 0 }}</p>
            <p :class="['text-xs mt-1', deltaClass(stats.period?.count, stats.previous_period?.count)]">
              {{ deltaLabel(stats.period?.count, stats.previous_period?.count) }}
              <span class="text-n-slate-10 ml-1">{{ t('CLOSURE_PANEL.PERIOD.VS_PREV') }}</span>
            </p>
            <p class="text-[10px] text-n-slate-9 mt-0.5">
              {{ t('CLOSURE_PANEL.PERIOD.PREV_LABEL') }}: {{ stats.previous_period?.count ?? 0 }}
            </p>
          </div>
          <div class="p-4 rounded-lg border border-n-weak">
            <p class="text-xs text-n-slate-11">{{ t('CLOSURE_PANEL.PERIOD.AMOUNT') }}</p>
            <p class="text-2xl font-semibold text-n-slate-12">{{ formatMoney(stats.period?.amount) }}</p>
            <p :class="['text-xs mt-1', deltaClass(stats.period?.amount, stats.previous_period?.amount)]">
              {{ deltaLabel(stats.period?.amount, stats.previous_period?.amount) }}
              <span class="text-n-slate-10 ml-1">{{ t('CLOSURE_PANEL.PERIOD.VS_PREV') }}</span>
            </p>
            <p class="text-[10px] text-n-slate-9 mt-0.5">
              {{ t('CLOSURE_PANEL.PERIOD.PREV_LABEL') }}: {{ formatMoney(stats.previous_period?.amount) }}
            </p>
          </div>
          <div class="p-4 rounded-lg border border-n-weak">
            <p class="text-xs text-n-slate-11">{{ t('CLOSURE_STATISTICS.TOTALS.AVERAGE_AMOUNT') }}</p>
            <p class="text-2xl font-semibold text-n-slate-12">{{ formatMoney(stats.average_managed_amount) }}</p>
          </div>
          <div class="p-4 rounded-lg border border-n-weak">
            <p class="text-xs text-n-slate-11">{{ t('CLOSURE_STATISTICS.TOTALS.AVG_HANDLING') }}</p>
            <p class="text-2xl font-semibold text-n-slate-12">
              {{ (() => {
                  const s = Math.max(0, Math.round(stats.average_handling_seconds || 0));
                  const h = Math.floor(s / 3600);
                  const m = Math.floor((s % 3600) / 60);
                  return h > 0 ? `${h}h ${m}m` : `${m}m`;
                })()
              }}
            </p>
          </div>
        </div>

        <div v-if="stats?.previous_period" class="text-xs text-n-slate-10 -mt-4">
          {{ t('CLOSURE_PANEL.PERIOD.PREV_RANGE') }}:
          {{ formatDate(stats.previous_period.since) }} – {{ formatDate(stats.previous_period.until) }}
        </div>

        <!-- ── Gráficos de tendencia ──────────────────────────────────────── -->
        <div v-if="trendData" class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div class="p-4 rounded-lg border border-n-weak">
            <p class="mb-3 text-sm font-medium text-n-slate-12">{{ t('CLOSURE_PANEL.TREND.TITLE') }} — {{ t('CLOSURE_PANEL.TREND.CLOSURES') }}</p>
            <div class="h-48">
              <Line :data="trendData" :options="chartOptions" />
            </div>
          </div>
          <div class="p-4 rounded-lg border border-n-weak">
            <p class="mb-3 text-sm font-medium text-n-slate-12">{{ t('CLOSURE_PANEL.TREND.TITLE') }} — {{ t('CLOSURE_PANEL.TREND.AMOUNT') }}</p>
            <div class="h-48">
              <Line :data="trendAmountData" :options="chartOptions" />
            </div>
          </div>
        </div>

        <!-- ── Ranking de agentes por monto ─────────────────────────────── -->
        <div v-if="stats" class="p-4 rounded-lg border border-n-weak">
          <p class="mb-3 text-sm font-medium text-n-slate-12">{{ t('CLOSURE_PANEL.RANKING.TITLE') }}</p>
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="text-left text-xs text-n-slate-11 border-b border-n-weak">
                  <th class="pb-2 pr-4">#</th>
                  <th class="pb-2 pr-4">{{ t('CLOSURE_PANEL.RANKING.AGENT') }}</th>
                  <th class="pb-2 pr-4 text-right">{{ t('CLOSURE_PANEL.RANKING.COUNT') }}</th>
                  <th class="pb-2 text-right">{{ t('CLOSURE_PANEL.RANKING.AMOUNT') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(agent, idx) in ranking" :key="agent.agent_id" class="border-b border-n-weak/40">
                  <td class="py-1.5 pr-4 text-n-slate-10">{{ idx + 1 }}</td>
                  <td class="py-1.5 pr-4 text-n-slate-12">{{ agent.agent_name }}</td>
                  <td class="py-1.5 pr-4 text-right text-n-slate-12">
                    {{ stats.by_agent?.find(a => a.agent_id === agent.agent_id)?.count ?? '—' }}
                  </td>
                  <td class="py-1.5 text-right font-medium text-n-slate-12">{{ formatMoney(agent.amount) }}</td>
                </tr>
                <tr v-if="!ranking.length">
                  <td colspan="4" class="py-4 text-center text-sm text-n-slate-10">—</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ── Proyección de cobros ──────────────────────────────────────── -->
        <div class="p-4 rounded-lg border border-n-weak">
          <div class="flex justify-between items-center mb-3">
            <p class="text-sm font-medium text-n-slate-12">{{ t('CLOSURE_PANEL.PROJECTION.TITLE') }}</p>
            <div class="flex gap-2">
              <NextButton :blue="projectionGroupBy === 'week'" :faded="projectionGroupBy !== 'week'" xs :label="t('CLOSURE_PANEL.PROJECTION.GROUP_WEEK')" @click="changeProjectionGroup('week')" />
              <NextButton :blue="projectionGroupBy === 'month'" :faded="projectionGroupBy !== 'month'" xs :label="t('CLOSURE_PANEL.PROJECTION.GROUP_MONTH')" @click="changeProjectionGroup('month')" />
            </div>
          </div>
          <div v-if="projection.length">
            <div class="h-48 mb-4">
              <Bar :data="projectionChartData" :options="chartOptions" />
            </div>
            <table class="w-full text-sm">
              <thead>
                <tr class="text-left text-xs text-n-slate-11 border-b border-n-weak">
                  <th class="pb-2 pr-4">{{ t('CLOSURE_PANEL.PROJECTION.PERIOD') }}</th>
                  <th class="pb-2 text-right">{{ t('CLOSURE_PANEL.PROJECTION.AMOUNT') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in projection" :key="row.period" class="border-b border-n-weak/40">
                  <td class="py-1.5 pr-4 text-n-slate-11">{{ row.period }}</td>
                  <td class="py-1.5 text-right font-medium text-n-slate-12">{{ formatMoney(row.amount) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="text-sm text-n-slate-10">{{ t('CLOSURE_PANEL.PROJECTION.EMPTY') }}</p>
        </div>

        <!-- ── DISTRIBUCIÓN DE CLASIFICACIONES ──────────────────────────── -->
        <div class="p-4 rounded-lg border border-n-weak">
          <div class="mb-3">
            <p class="text-sm font-medium text-n-slate-12">{{ t('CLOSURE_PANEL.CLASSIFICATIONS.TITLE') }}</p>
            <p class="text-xs text-n-slate-10 mt-0.5">{{ t('CLOSURE_PANEL.CLASSIFICATIONS.SUBTITLE') }}</p>
          </div>

          <div v-if="!stats" class="py-6 text-center text-sm text-n-slate-10">
            {{ t('CLOSURE_PANEL.CLASSIFICATIONS.EMPTY') }}
          </div>

          <div v-else class="grid grid-cols-1 gap-6 md:grid-cols-2">
            <!-- Por grupo de cobranza (barras verticales con colores) -->
            <div>
              <p class="text-xs font-medium text-n-slate-11 mb-2 uppercase tracking-wide">
                {{ t('CLOSURE_PANEL.CLASSIFICATIONS.BY_GROUP') }}
              </p>
              <div v-if="classificationByGroupData" class="h-40">
                <Bar :data="classificationByGroupData" :options="chartOptions" />
              </div>
              <p v-else class="text-sm text-n-slate-10">{{ t('CLOSURE_PANEL.CLASSIFICATIONS.EMPTY') }}</p>
              <!-- Leyenda numérica -->
              <div v-if="stats?.by_group" class="mt-2 flex flex-col gap-1">
                <div
                  v-for="(count, key) in stats.by_group"
                  :key="key"
                  class="flex justify-between items-center text-xs"
                >
                  <span class="text-n-slate-11">{{ GROUP_LABELS[key] || key }}</span>
                  <span class="font-medium text-n-slate-12">{{ count }}</span>
                </div>
              </div>
            </div>

            <!-- Por categoría de tipificación (barras horizontales) -->
            <div>
              <p class="text-xs font-medium text-n-slate-11 mb-2 uppercase tracking-wide">
                {{ t('CLOSURE_PANEL.CLASSIFICATIONS.BY_REASON') }}
              </p>
              <div v-if="classificationByReasonData" class="h-56">
                <Bar :data="classificationByReasonData" :options="horizontalChartOptions" />
              </div>
              <p v-else class="text-sm text-n-slate-10">{{ t('CLOSURE_PANEL.CLASSIFICATIONS.EMPTY') }}</p>
            </div>
          </div>
        </div>

        <!-- ── Tabla de cierres (filtros extra: solo monto) ──────────────── -->
        <div class="p-4 rounded-lg border border-n-weak">
          <div class="flex justify-between items-center mb-3">
            <p class="text-sm font-medium text-n-slate-12">{{ t('CLOSURE_PANEL.TABLE.TITLE') }}</p>
            <span class="text-xs text-n-slate-10">{{ totalCount }} {{ t('CLOSURE_PANEL.TABLE.TOTAL_LABEL') }}</span>
          </div>

          <div class="flex gap-3 items-end mb-4 flex-wrap">
            <div class="flex flex-col gap-1">
              <label class="text-xs text-n-slate-11">{{ t('CLOSURE_PANEL.FILTER.MANAGED_AMOUNT_MIN') }}</label>
              <NextInput v-model="managedAmountMin" type="number" placeholder="0" class="w-32" />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-xs text-n-slate-11">{{ t('CLOSURE_PANEL.FILTER.MANAGED_AMOUNT_MAX') }}</label>
              <NextInput v-model="managedAmountMax" type="number" placeholder="∞" class="w-32" />
            </div>
            <NextButton faded slate sm :is-loading="isLoadingTable" :label="t('CLOSURE_PANEL.FILTER.APPLY')" @click="() => { currentPage = 1; loadTable(); }" />
          </div>

          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="text-left text-xs text-n-slate-11 border-b border-n-weak">
                  <th class="pb-2 pr-4">{{ t('CLOSURE_PANEL.TABLE.CONVERSATION') }}</th>
                  <th class="pb-2 pr-4">{{ t('CLOSURE_PANEL.TABLE.AGENT') }}</th>
                  <th class="pb-2 pr-4">{{ t('CLOSURE_PANEL.TABLE.REASON') }}</th>
                  <th class="pb-2 pr-4 text-right">{{ t('CLOSURE_PANEL.TABLE.AMOUNT') }}</th>
                  <th class="pb-2 text-right">{{ t('CLOSURE_PANEL.TABLE.CLOSED_AT') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="c in closures" :key="c.id" class="border-b border-n-weak/40 hover:bg-n-alpha-1">
                  <td class="py-1.5 pr-4 text-n-slate-12">#{{ c.conversation_display_id }}</td>
                  <td class="py-1.5 pr-4 text-n-slate-11">{{ c.agent?.name ?? '—' }}</td>
                  <td class="py-1.5 pr-4 text-n-slate-11">{{ c.closure_reason ?? '—' }}</td>
                  <td class="py-1.5 pr-4 text-right text-n-slate-12">
                    {{ c.managed_amount != null ? formatMoney(c.managed_amount) : '—' }}
                  </td>
                  <td class="py-1.5 text-right text-n-slate-11">{{ formatDate(c.closed_at) }}</td>
                </tr>
                <tr v-if="!closures.length && !isLoadingTable">
                  <td colspan="5" class="py-4 text-center text-sm text-n-slate-10">
                    {{ t('CLOSURE_PANEL.TABLE.EMPTY') }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="totalPages > 1" class="flex justify-between items-center mt-4">
            <NextButton faded slate xs :label="t('CLOSURE_PANEL.FILTER.PREV')" :disabled="currentPage <= 1" @click="prevPage" />
            <span class="text-xs text-n-slate-11">
              {{ t('CLOSURE_PANEL.TABLE.PAGE_OF', { page: currentPage, total: totalPages }) }}
            </span>
            <NextButton faded slate xs :label="t('CLOSURE_PANEL.FILTER.NEXT')" :disabled="currentPage >= totalPages" @click="nextPage" />
          </div>
        </div>

      </div>
    </template>
  </SettingsLayout>
</template>
